import { Stack } from '@mui/material';
import { useLanguageSelector } from '@retail/app/hooks';
import {
  useIsFeatureEnabled,
  usePermissionsForCurrentUser,
  useUserBrands,
} from '@retail/app/stores/selected-context';
import {
  createSalesPriceConditionSchema,
  SalesPriceConditionsTable,
} from '@retail/calculus/components';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { ConditionsNew } from '@retail/calculus/types';
import { AddButton, CalculusActionPortal, DeleteDialog } from '@retail/components';
import { SupplierBase } from '@retail/suppliers/types';
import { useDisclosure } from '@shared/hooks';
import { useState } from 'react';
import { OutlinedButton, UniversalFormProvider } from '@shared/components';
import { CreateSalesPriceCondition } from './CreateSalesPriceCondition';
import { EditSalesPriceConditionDialogContainer } from './EditSalesPriceCondition';
import { ExportConditionsMenu, useSalesPriceConditionExport } from './Export';
import { UploadMarkupConditionsDialog } from '../MarkupConditions/UploadMarkupConditions';
import { useConditionActions } from '@retail/calculus/context';

interface Props {
  assortmentId: number;
  selectedSupplier?: SupplierBase;
  conditions: ConditionsNew[];
  isFetching: boolean;
}

export function SalesPriceConditionsContainer({
  assortmentId,
  selectedSupplier,
  conditions,
  isFetching,
}: Props) {
  const { language } = useLanguageSelector();
  const t = useCalculusTFunction();
  const { removeCondition } = useConditionActions();

  const { selectedBrand } = useUserBrands();
  const includeUnit = selectedBrand.wholesaler;

  const { isOpen: isUploadOpen, onToggle: toggleUploadDialog } = useDisclosure();

  const localPriceSuggestionIsEnabled = useIsFeatureEnabled('local-price-suggestions');

  const { userCanCreate, userCanUpdate, userCanDelete, userCanRead } =
    usePermissionsForCurrentUser();
  const userCanCreateMarkup = userCanCreate('PRICE_CALCULATION__MARKUP');
  const userCanUpdateMarkup = userCanUpdate('PRICE_CALCULATION__MARKUP');
  const userCanCreateSalesPriceConditions = userCanCreate(
    'PRICE_CALCULATION__SALES_PRICE_CONDITION'
  );
  const userCanUpdateSalesPriceConditions = userCanUpdate(
    'PRICE_CALCULATION__SALES_PRICE_CONDITION'
  );
  const userCanDeleteSalesPriceConditions = userCanDelete(
    'PRICE_CALCULATION__SALES_PRICE_CONDITION'
  );
  const userCanReadLocalPriceSuggetions =
    userCanRead('PRICE_SUGGESTIONS') && localPriceSuggestionIsEnabled;

  const { isOpen: isCreateOpen, onToggle: toggleCreateDialog } = useDisclosure();

  const [editCondition, setEdit] = useState<ConditionsNew | undefined>();
  const [deactivateConditionIds, setDeactivate] = useState<number[] | undefined>();
  const [deleteConditionIds, setDelete] = useState<number[] | undefined>();
  const { exportMarkupAsXLSX, exportSalesPriceAsXLSX } = useSalesPriceConditionExport(conditions);
  return (
    <Stack flexGrow={1}>
      <CalculusActionPortal>
        <ExportConditionsMenu
          exportMarkups={exportMarkupAsXLSX}
          exportSalesPriceConditions={exportSalesPriceAsXLSX}
        />
        {selectedSupplier && (
          <OutlinedButton
            color="secondary"
            size="small"
            onClick={toggleUploadDialog}
            disabled={!userCanCreateMarkup}
          >
            {t('calculus.actions.uploadMarkup')}
          </OutlinedButton>
        )}
        {userCanCreateSalesPriceConditions && (
          <AddButton onClick={toggleCreateDialog}>
            {!userCanCreateMarkup
              ? t('calculus.followPriceSuggestionCondition')
              : t('calculus.salesPriceCondition.newSalesPriceCondition')}
          </AddButton>
        )}
      </CalculusActionPortal>

      <SalesPriceConditionsTable
        conditions={conditions}
        language={language}
        loading={isFetching}
        calculationLevel={selectedSupplier ? 'supplier' : 'assortment'}
        createCondition={userCanCreateSalesPriceConditions ? toggleCreateDialog : undefined}
        editCondition={userCanUpdateSalesPriceConditions ? setEdit : undefined}
        deactivateCondition={userCanUpdateSalesPriceConditions ? setDeactivate : undefined}
        deleteCondition={userCanDeleteSalesPriceConditions ? setDelete : undefined}
      />

      {isCreateOpen && (
        <UniversalFormProvider
          schema={createSalesPriceConditionSchema(t, includeUnit)}
          defaultValues={{
            ...createSalesPriceConditionSchema(t, includeUnit).getDefault(),
            level: selectedSupplier ? '' : 'assortment',
          }}
        >
          <CreateSalesPriceCondition
            supplierNumber={selectedSupplier?.supplierNumber}
            salesPriceConditions={conditions}
            includeMarkup={userCanCreateMarkup}
            includeLocalPriceSuggestion={userCanReadLocalPriceSuggetions}
            includeUnit={includeUnit}
            onClose={toggleCreateDialog}
          />
        </UniversalFormProvider>
      )}

      {!!editCondition && (
        <EditSalesPriceConditionDialogContainer
          open
          assortmentId={assortmentId}
          condition={editCondition}
          includeMarkup={userCanUpdateMarkup}
          supplierId={selectedSupplier?.supplierNumber}
          onClose={() => setEdit(undefined)}
        />
      )}
      {!!deactivateConditionIds && (
        <DeleteDialog
          dialogTitle={t('calculus.actions.deactivate')}
          message={t('calculus.salesPriceCondition.deactivateConditionConfirmation')}
          isOpen
          onDelete={() => removeCondition(deactivateConditionIds, 'deactivate')}
          onClose={() => setDeactivate(undefined)}
        />
      )}
      {!!deleteConditionIds && (
        <DeleteDialog
          dialogTitle={t('calculus.actions.deleteFromDraft')}
          message={t('calculus.salesPriceCondition.deleteConditionConfirmation')}
          isOpen
          onDelete={() => removeCondition(deleteConditionIds, 'delete')}
          onClose={() => setDelete(undefined)}
        />
      )}

      {selectedSupplier && userCanCreateMarkup && (
        <UploadMarkupConditionsDialog
          open={isUploadOpen}
          onClose={toggleUploadDialog}
          mgSupplierId={selectedSupplier.supplierNumber}
          currentConditions={conditions}
        />
      )}
    </Stack>
  );
}
